<template>
  <button
    ref="buttonRef"
    v-loading="loading"
    class="button"
    :type="nativeType"
    :class="[
      `button--${props.type || 'default'}`,
      `button--${props.size || 'medium'}`,
      props.contentIcon ? 'is-icon' : '',
      props.loading ? 'is-loading' : '',
      props.disabled ? 'is-disabled' : '',
    ]"
    :disabled="props.disabled || props.loading"
  >
    <Icon
      v-if="props.contentIcon"
      :name="props.contentIcon"
      class="w-5 h-auto"
    />
    <template v-else-if="!props.contentIcon">
      <Icon
        v-if="props.iconLeft"
        :name="props.iconLeft"
        class="w-5 h-auto"
      />
      <span
        class="slot"
        :class="props.iconLeft ? 'hidden sm:block' : ''"
      >
        <slot />
      </span>
      <Icon
        v-if="props.iconRight"
        :name="props.iconRight"
        class="w-5 h-auto"
      />
    </template>

    <el-tooltip
      v-if="isMounted && props.disabled && props.disabledTooltip"
      :content="props.disabledTooltip"
      trigger="hover"
      virtual-triggering
      :virtual-ref="buttonRef"
    />
  </button>
</template>

<script setup>
const props = defineProps(buildProps({
  type: {
    type: String,
    values: [
      'regular',
      'regular-outline',
      'regular-text',
      'primary',
      'primary-outline',
      'primary-text',
      'danger',
      'danger-outline',
      'danger-text',
    ],
    default: 'regular',
  },
  nativeType: {
    type: String,
    values: ['button', 'submit', 'reset'],
    default: 'button',
  },
  size: {
    type: String,
    values: ['small', 'medium', 'large'],
    default: 'medium',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  contentIcon: {
    type: String,
    default: '',
  },
  iconLeft: {
    type: String,
    default: '',
  },
  iconRight: {
    type: String,
    default: '',
  },
  disabledTooltip: {
    type: String,
    default: '',
  },
}));

// Refs
const buttonRef = ref(null);
const isMounted = useMounted();
</script>

<style scoped>
.button {
  @apply flex items-center justify-center gap-2;
  @apply rounded-lg font-medium;
  @apply transition ease-in-out;
  @apply shadow-sm;
  @apply whitespace-nowrap;

  &:has(.slot:empty) {
    @apply gap-0;
  }

  /* Sizes */
  &--small {
    @apply text-sm px-2 h-[1.875rem] rounded-md;

    :deep(.el-loading-spinner) {
      --el-loading-spinner-size: 20px;
    }

    &.is-icon {
      @apply w-8 px-0;
    }
  }

  &--medium {
    @apply text-sm px-2 sm:px-3 h-9;

    :deep(.el-loading-spinner) {
      --el-loading-spinner-size: 22px;
    }

    &.is-icon {
      @apply w-9 px-0;
    }
  }

  &--large {
    @apply text-base px-4 h-11;

    :deep(.el-loading-spinner) {
      --el-loading-spinner-size: 28px;
    }

    &.is-icon {
      @apply w-11 px-0;
    }
  }

  /* Types */
  &--regular {
    @apply border border-transparent text-gray-700 bg-white;
    @apply shadow-none;

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus-visible {
      @apply bg-gray-50;
    }

    &.is-disabled {
      @apply text-gray-300;
    }
  }

  &--regular-outline {
    @apply border border-gray-300 text-gray-700 bg-white;

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus-visible {
      @apply bg-gray-50;
    }

    &.is-disabled {
      @apply text-gray-200 border-gray-200 shadow-none;
    }
  }

  &--regular-text {
    @apply border-none p-0 text-gray-700;
    @apply shadow-none;

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus-visible {
      @apply text-gray-800;
    }

    &.is-disabled {
      @apply text-gray-300;
    }
  }

  &--primary {
    @apply border border-primary-500 bg-primary-500 text-white;

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus-visible {
      @apply bg-primary-600 border-primary-600;
    }

    &.is-disabled {
      @apply bg-gray-200 text-gray-400 border-gray-200;
      @apply shadow-none;
    }
  }

  &--primary-outline {
    @apply border border-gray-300 text-primary-500 bg-white;

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus-visible {
      @apply bg-gray-50 text-primary-600;
    }

    &.is-disabled {
      @apply text-gray-300 border-gray-200;
      @apply shadow-none;
    }
  }

  &--primary-text {
    @apply border-none p-0 text-primary-500;
    @apply shadow-none;

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus-visible {
      @apply text-primary-600;
    }

    &.is-disabled {
      @apply text-gray-300;
    }
  }

  &--danger {
    @apply border border-red-500 bg-red-500 text-white;

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus-visible {
      @apply border-red-600 bg-red-600;
    }

    &.is-disabled {
      @apply bg-transparent text-gray-300 border-transparent;
      @apply shadow-none;
    }
  }

  &--danger-outline {
    @apply border border-red-100 text-red-600 bg-white;

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus-visible {
      @apply bg-red-50;
    }

    &.is-disabled {
      @apply bg-transparent text-gray-300 border-gray-300;
      @apply shadow-none;
    }
  }

  &--danger-text {
    @apply border-none p-0 text-red-500;
    @apply shadow-none;

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus-visible {
      @apply text-red-600;
    }

    &.is-disabled {
      @apply text-gray-300;
    }
  }

  &.is-loading {
    @apply shadow-none;
  }

  :deep(.el-loading-mask) {
    @apply rounded-lg;
    transform: scaleX(1.04) scaleY(1.0666);
  }
}
</style>
